import { useContext, useEffect, useState } from "react"
import { NotificationContext } from "@context"
import { CREATE_NOTIFICATION_MUTATION } from "@graphql"
import { addNotificationToCache, getGraphQLErrorMessage } from "@helpers"
import { useInvitationsLoader } from "@hooks"
import { useMutation } from "@apollo/client"
import PropTypes from "prop-types"

import { Step1, Step2, Step3, Step4 } from "./Steps"

const bookingConfirmationDescription = "A standard booking confirmation email."

const defaultConfiguration = {
  alertType: null,
  title: "",
  type: null,
  description: "",
  booking_confirmations_outstanding_only: false
}

const Notifications = ({ 
  event, 
  handleClose, 
  handleNotificationCreated, 
  initialConfigurationOverrides = null,
  notificationTypes, 
  notificationRecipientTypes, 
  notificationStatus 
}) => {
  const initialConfiguration = initialConfigurationOverrides 
    ? {...defaultConfiguration, ...initialConfigurationOverrides } 
    : defaultConfiguration

  const [currentStep, setCurrentStep] = useState(1)
  const [configuration, setConfiguration] = useState(initialConfiguration)
  
  const [isComplete, setIsComplete] = useState(false)
  const [isStep2Submitting, setIsStep2Submitting] = useState(false)
  const [isStep4Submitting, setIsStep4Submitting] = useState(false)
  const {notify} = useContext(NotificationContext)

  const {
    invitations,
    invitationSelections,
    filter,
    searchTerm,
    selectedGroupIds,
    setFilter,
    setSearchTerm,
    setSelectedGroupIds,
    page,
    setInvitationSelections,
    loadInvitations
  } = useInvitationsLoader({ eventId: event.id, notify })

  const [createNotification] = useMutation(CREATE_NOTIFICATION_MUTATION, {
    onCompleted: (data) => {
      handleNotificationCreated(data.createNotification).then(() => {
        setIsStep4Submitting(false)
        setIsComplete(true)
      })
    },

    onError: (error) => {      
      notify({ 
        type: "danger", 
        title: "Error Creating Notification",
        content: getGraphQLErrorMessage(error, "There was an error creating the notification."),
        open: true
      })

      setIsStep4Submitting(false)
    },
    
    update: (cache, { data: { createNotification } }) => addNotificationToCache(cache, createNotification, event.id)
  })
      
  useEffect(() => {
    if (configuration?.type?.code === "BOOKING_CONFIRMATION_EMAIL" && configuration?.booking_confirmations_outstanding_only) {
      setFilter("BOOKING_CONFIRMATION_EMAIL_NEVER_SENT")
    } else if (configuration?.type?.code === "BOOKING_CONFIRMATION_EMAIL") {
      setFilter("BOOKED_ONLY")
    } else {
      setFilter("ALL") 
    }
  }, [configuration?.type?.code, configuration?.booking_confirmations_outstanding_only, setFilter])

  useState(() => {
    setSelectedGroupIds(event.sessions.flatMap(session => session.groups.map(group => group.id)))
  }, [event.sessions, setSelectedGroupIds])
  
  const getBookingConfirmationTitle = (userIds) => {
    if (userIds.length === 1) {
      return invitations.find(invitation => invitation.user_id === userIds[0])?.guardian || "Booking Confirmation"
    }

    return "Multiple Guardians"
  }

  const handleClearAllInvitations = () => {
    const newInvitationSelections = invitationSelections.map((selection) => ({ ...selection, selected: false }))
    setInvitationSelections(newInvitationSelections)
  }

  const handleSelectAllInvitations = () => {
    const newInvitationSelections = invitationSelections.map((selection) => ({ ...selection, selected: true }))
    setInvitationSelections(newInvitationSelections)
  }

  const handleLoadMore = async () => {
    loadInvitations(null, null, null, page+1, false, true)
  }

  const handleSearchChange = (searchTerm) => {
    loadInvitations(null, searchTerm, null, 1, false)
  }

  const handleStep1Submit = (newConfiguration) => {
    setConfiguration(newConfiguration)
    setCurrentStep(2)
  }

  const handleStep2Previous = (groupIds) => {
    setSelectedGroupIds(groupIds)
    setCurrentStep(1)
  }

  const handleStep2Submit = async (groupIds) => {
    setIsStep2Submitting(true)
    await loadInvitations(filter, null, groupIds, 1, false, false)
    setIsStep2Submitting(false)
    setCurrentStep(3)
  }

  const handleStep3Previous = () => {
    setSearchTerm("")
    setCurrentStep(2)
  }

  const handleStep3Submit = () => {
    setCurrentStep(4)
  }

  const handleStep4Submit = () => {
    setIsStep4Submitting(true)
    const userIds = invitationSelections.filter((selection) => selection.selected).map((selection) => selection.user_id)
    const type = configuration.type.code
    const title = type !== "BOOKING_CONFIRMATION_EMAIL" ? configuration.title : getBookingConfirmationTitle(userIds)

    createNotification({
      variables: {
        input: {
          event_id: event.id,
          title: title,
          description: type === "BOOKING_CONFIRMATION_EMAIL" ? bookingConfirmationDescription : configuration.description,
          user_ids: userIds,
          type_id: configuration.type.id,
          status_id: configuration?.alertType?.id || 1,
          recipient_type_id: notificationRecipientTypes.find((type) => type.code === "PARENTS").id,
        }
      }
    })
  }

  const handleFinish = () => {
    if (isComplete) {
      handleClose()
    } else {
      handleStep4Submit()
    }
  }

  const handleToggleSelectedInvitation = (invitationId, value) => {
    setInvitationSelections(prev => prev.map(invitationSelection => {
      return invitationSelection.invitation_id === invitationId ? { ...invitationSelection, selected: value } : invitationSelection
    }))
  }

  return (
    <div>
      {currentStep === 1 && (
        <Step1
          configuration={configuration}
          handleClose={() => handleClose()}
          handleNext={(configuration) => handleStep1Submit(configuration)}
          notificationTypes={notificationTypes}
          notificationRecipientTypes={notificationRecipientTypes}
          notificationStatus={notificationStatus}
        />
      )}

      {currentStep === 2 && (
        <Step2
          currentSelectedGroupIds={selectedGroupIds}
          handlePrevious={(groupIds) => handleStep2Previous(groupIds)}
          handleNext={(groupIds) => handleStep2Submit(groupIds)}
          isSubmitting={isStep2Submitting}
          sessions={event.sessions}
        />
      )}

      {currentStep === 3 && (
        <Step3
          handleClearAll={() => handleClearAllInvitations()}
          handleLoadMore={() => handleLoadMore()}
          handlePrevious={() => handleStep3Previous()}
          handleNext={() => handleStep3Submit()}
          handleSearchChange={(searchTerm) => handleSearchChange(searchTerm)}
          handleSelectAll={() => handleSelectAllInvitations()}
          handleToggleSelected={(invitationId, value) => handleToggleSelectedInvitation(invitationId, value)}
          invitations={invitations}
          handleSearchClear={() => handleSearchChange("")}
          invitationSelections={invitationSelections}
          searchTerm={searchTerm}
        />
      )}

      {currentStep === 4 && (
        <Step4
          counts={{ 
            deselected: invitationSelections.filter(selection => !selection.selected).length, 
            selected: invitationSelections.filter(selection => selection.selected).length, 
            total: invitationSelections.length 
          }}
          notificationType={configuration.type}
          handleClose={() => handleClose()}
          handleFinish={() => handleFinish()}
          handlePrevious={() => setCurrentStep(3)}
          isComplete={isComplete}
          isSubmitting={isStep4Submitting}
        />
      )}
    </div>
  )
}

Notifications.propTypes = {
  event: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleNotificationCreated: PropTypes.func.isRequired,
  initialConfigurationOverrides: PropTypes.shape({
    alertType: PropTypes.object,
    title: PropTypes.string,
    type: PropTypes.object,
    description: PropTypes.string,
    booking_confirmations_outstanding_only: PropTypes.bool
  }),
  notificationTypes: PropTypes.array.isRequired,
  notificationRecipientTypes: PropTypes.array.isRequired,
  notificationStatus: PropTypes.array.isRequired
}

export default Notifications